import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../api/axios";
import { ArrowLeft, Plus, Save, Trash2 } from "lucide-react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Swal from "sweetalert2";
import { formatCurrencyCOP } from "../../utils/format";
import Select from "react-select";

const RegisterExit = () => {
  const navigate = useNavigate();
  const [exitDate, setExitDate] = useState("");
  const [clientId, setClientId] = useState("");
  const [clients, setClients] = useState([]);
  const [products, setProducts] = useState([]);
  const [productVariants, setProductVariants] = useState([]);
  const [errors, setErrors] = useState({});
  const [details, setDetails] = useState([]);

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [form, setForm] = useState({
    productId: "",
    variantId: "",
    quantity: "",
    available: "",
    unitPrice: "",
  });

  const productOptions = products.map((p) => ({
    value: p.id,
    label: p.name,
  }));

  const variantOptions = productVariants.map((v) => ({
    value: v.id,
    label: `Lote: ${v.batch_number} | INVIMA: ${v.invima_code} | Vence: ${v.expiration_date ? new Date(v.expiration_date).toLocaleDateString("es-CO") : "NR"
      } | Marca: ${v.brand?.name || "Sin marca"} | Presentación: ${v.presentation?.name || "Sin presentación"}`,
  }));



  useEffect(() => {
    api.get("/clients").then((res) => {
      setClients(res.data);
    });
    api.get("/products").then((res) => {
      setProducts(res.data);
    });
  }, []);

  const handleVariantSelect = (variantId) => {
    const variant = productVariants.find((v) => v.id === parseInt(variantId));
    if (variant) {
      setForm((prev) => ({
        ...prev,
        variantId,
        unitPrice: variant.unit_price,
        available: variant.inventories?.[0]?.current_quantity || 0,
      }));
    }
  };

  const handleProductChange = async (productId) => {
    setForm((prev) => ({ ...prev, productId, variantId: "", quantity: "", available: "", unitPrice: "" }));
    setProductVariants([]);
    if (!productId) return;
    try {
      const res = await api.get(`/exits/variants-with-stock/${productId}`);
      setProductVariants(res.data);
    } catch (error) {
      toast.error("No se pudieron cargar las variantes");
    }
  };

  const handlePreSubmit = () => {
    const newErrors = {};

    if (!exitDate) newErrors.exitDate = "Selecciona la fecha de salida";
    if (!clientId) newErrors.clientId = "Selecciona un cliente";

    // Validamos si la tabla está vacía
    if (details.length === 0) {
      newErrors.details = "Agrega al menos un producto";
      // Mostramos el toast específico para la tabla vacía
      toast.error("No puedes guardar una salida sin productos en la lista");
      return; // Detenemos la ejecución aquí
    }

    setErrors(newErrors);

    // Validamos si hay otros errores (fecha o cliente)
    if (Object.keys(newErrors).length > 0) {
      toast.error("Corrige los errores antes de guardar");
      return;
    }

    // Si todo está bien, mostramos el modal
    setShowConfirmModal(true);
  };

  const handleAddDetail = () => {
    const { productId, variantId, quantity, unitPrice, available } = form;
    const newErrors = {};

    // --- VALIDACIONES INICIALES ---
    if (!productId) newErrors.productId = "Selecciona un producto";
    if (!variantId) newErrors.variantId = "Selecciona una variante";
    if (!quantity || parseInt(quantity) <= 0) {
      newErrors.quantity = "Ingresa una cantidad válida";
    } else {
      const cantidadIngresada = parseInt(quantity);
      const cantidadDisponible = parseInt(available);

      // Calcular cuánto se ha agregado de esta variante en total (sumando todas las filas si hubiera varias)
      const cantidadYaAgregada = details
        .filter((d) => d.variantId === variantId)
        .reduce((sum, d) => sum + parseInt(d.quantity), 0);

      if (cantidadIngresada + cantidadYaAgregada > cantidadDisponible) {
        newErrors.quantity = `Supera el stock. Disponible: ${cantidadDisponible}, Ya en lista: ${cantidadYaAgregada}`;
      }
    }

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    // --- LÓGICA DE AGREGAR O ACTUALIZAR ---

    // 1. Buscamos si la variante ya está en la tabla
    const existingDetailIndex = details.findIndex((d) => d.variantId === variantId);

    if (existingDetailIndex !== -1) {
      // 2. Si existe, creamos una copia de la lista y actualizamos la cantidad
      const updatedDetails = [...details];
      updatedDetails[existingDetailIndex].quantity =
        parseInt(updatedDetails[existingDetailIndex].quantity) + parseInt(quantity);

      setDetails(updatedDetails);
    } else {
      // 3. Si no existe, lo agregamos normal
      const product = products.find((p) => p.id === parseInt(productId));
      const variant = productVariants.find((v) => v.id === parseInt(variantId));

      const newDetail = {
        productId,
        variantId,
        productName: product.name,
        brandName: variant.brand?.name || "Sin marca",
        presentationName: variant.presentation?.name || "Sin presentación",
        invimaCode: variant.invima_code,
        batchNumber: variant.batch_number,
        expirationDate: variant.expiration_date,
        quantity: parseInt(quantity), // Aseguramos que sea número
        unitPrice,
        available: variant.inventories?.[0]?.current_quantity || 0,
      };

      setDetails([...details, newDetail]);
    }

    // --- LIMPIEZA DEL FORMULARIO ---
    setForm({
      productId: "",
      variantId: "",
      quantity: "",
      available: "",
      unitPrice: "",
    });
    setProductVariants([]);
    setErrors({});
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setShowConfirmModal(false);

    try {
      const payload = {
        date: exitDate,
        client_id: clientId,
        user_id: 1,
        total_exit: details.reduce(
          (total, d) => total + parseFloat(d.unitPrice) * parseInt(d.quantity),
          0
        ),
        details: details.map((d) => ({
          product_id: d.productId,
          variant_id: d.variantId,
          quantity: parseInt(d.quantity),
        })),
      };

      await api.post("/exits", payload);

      // ✅ Animación de Éxito
      Swal.fire({
        title: "¡Salida Registrada!",
        text: "El inventario ha sido actualizado correctamente.",
        icon: "success",
        timer: 2000,
        showConfirmButton: false,
        willClose: () => {
          navigate("/exits");
        },
      });
    } catch (error) {
      console.error("Error al registrar la salida:", error);
      toast.error("Error al registrar la salida");
      setIsSubmitting(false);
    }
  };

  const handleRemoveDetail = (index) => {
    const newDetails = details.filter((_, i) => i !== index);
    setDetails(newDetails);
    toast.info("Producto removido de la lista");
  };

  const decreaseQuantity = (index) => {
    const detail = details[index];
    const newQuantity = detail.quantity - 1;
    if (newQuantity <= 0) {
      handleRemoveDetail(index);
    } else {
      const updatedDetails = [...details];
      updatedDetails[index].quantity = newQuantity;
      setDetails(updatedDetails);
    }
  };

  const increaseQuantity = (index) => {
    const detail = details[index];
    const available = detail.available;
    const cantidadYaAgregada = details
      .filter((d, idx) => d.variantId === detail.variantId && idx !== index)
      .reduce((sum, d) => sum + parseInt(d.quantity), 0);
    const maxPermitido = available - cantidadYaAgregada;
    if (detail.quantity + 1 <= maxPermitido) {
      const updatedDetails = [...details];
      updatedDetails[index].quantity += 1;
      setDetails(updatedDetails);
    } else {
      toast.error("No hay suficiente stock disponible");
    }
  };

  return (
    <div className="bg-white p-6 rounded-xl shadow-md max-w-7xl mx-auto">
      <button
        onClick={() => navigate("/exits")}
        className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6"
      >
        <ArrowLeft size={20} />
        Volver a salidas
      </button>

      <h2 className="text-2xl text-center font-bold mb-4">Registrar Salida</h2>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="font-semibold">Fecha de Salida:</label>
          <input
            type="date"
            value={exitDate}
            onChange={(e) => setExitDate(e.target.value)}
            className={`w-full mt-1 border rounded px-3 py-2 ${errors.exitDate ? "border-red-500" : ""}`}
          />
          {errors.exitDate && (
            <p className="text-red-500 text-sm mt-1">{errors.exitDate}</p>
          )}
        </div>

        <div>
          <label className="font-semibold">Cliente:</label>
          <select
            value={clientId}
            onChange={(e) => setClientId(e.target.value)}
            className={`w-full mt-1 border rounded px-3 py-2 ${errors.clientId ? "border-red-500" : ""}`}
          >
            <option value="">Seleccionar cliente</option>
            {clients.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
          {errors.clientId && (
            <p className="text-red-500 text-sm mt-1">{errors.clientId}</p>
          )}
        </div>
      </div>

      <hr className="my-6" />

      <h3 className="text-xl text-center font-semibold mb-4">
        Agregar Producto
      </h3>

      {/* Contenedor de Formulario de Producto */}
      <div className="flex flex-col gap-4">

        {/* 1. Insumo (Producto) - Ancho Completo */}
        <div className="w-full">
          <label className="font-semibold">Producto:</label>
          <Select
            options={productOptions}
            placeholder="Buscar producto..."
            isClearable
            value={productOptions.find((opt) => opt.value === form.productId) || null}
            onChange={(selected) => handleProductChange(selected ? selected.value : "")}
            className="mt-1"
            classNamePrefix="react-select"
            styles={{
              control: (base) => ({
                ...base,
                borderColor: errors.productId ? "#ef4444" : "#d1d5db",
                minHeight: "42px",
              }),
            }}
          />
          {errors.productId && (
            <p className="text-red-500 text-sm mt-1">{errors.productId}</p>
          )}
        </div>

        {/* 2. Variante - Ancho Completo debajo de Insumo */}
        <div className="w-full">
          <label className="font-semibold">Variante (Buscar por Lote o INVIMA):</label>
          <Select
            options={variantOptions}
            placeholder={
              form.productId
                ? "Escribe el lote o selecciona una variante..."
                : "Primero selecciona un producto"
            }
            isClearable
            isDisabled={!form.productId} // Se deshabilita si no hay producto
            value={variantOptions.find((opt) => opt.value === parseInt(form.variantId)) || null}
            onChange={(selected) => handleVariantSelect(selected ? selected.value : "")}
            className="mt-1"
            classNamePrefix="react-select"
            noOptionsMessage={() => "No se encontraron variantes"}
            styles={{
              control: (base) => ({
                ...base,
                borderColor: errors.variantId ? "#ef4444" : "#d1d5db",
                minHeight: "42px",
              }),
            }}
          />
          {errors.variantId && (
            <p className="text-red-500 text-sm mt-1">{errors.variantId}</p>
          )}
        </div>

        {/* 3. Cantidades - Una a la izquierda y otra a la derecha */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="font-semibold">Cantidad disponible:</label>
            <input
              type="number"
              value={form.available || ""}
              disabled
              className="w-full mt-1 border rounded px-3 py-2 bg-gray-100"
            />
          </div>

          <div>
            <label className="font-semibold">Cantidad a entregar:</label>
            <input
              type="number"
              value={form.quantity}
              onChange={(e) => setForm({ ...form, quantity: e.target.value })}
              min="1"
              step="1"
              className={`w-full mt-1 border rounded px-3 py-2 ${errors.quantity ? "border-red-500" : ""}`}
            />
            {errors.quantity && (
              <p className="text-red-500 text-sm mt-1">{errors.quantity}</p>
            )}
          </div>
        </div>
      </div>

      <button
        onClick={handleAddDetail}
        className="mt-6 bg-blue-600 text-white px-6 py-2 rounded-lg flex items-center gap-2 hover:bg-blue-700 transition-colors shadow-sm"
      >
        <Plus size={18} />
        Agregar Producto
      </button>

      <div className="overflow-x-auto rounded-xl shadow mt-6">
        <table className="min-w-full bg-white table-auto text-center">
          <thead>
            <tr className="bg-gray-100 text-gray-700 text-sm uppercase tracking-wider">
              <th className="px-6 py-3">Producto</th>
              <th className="px-6 py-3">Marca</th>
              <th className="px-6 py-3">Presentación</th>
              <th className="px-6 py-3">INVIMA</th>
              <th className="px-6 py-3">Lote</th>
              <th className="px-6 py-3">Vencimiento</th>
              <th className="px-6 py-3">Cantidad</th>
              <th className="px-6 py-3">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {details.length === 0 ? (
              <tr>
                <td
                  colSpan="8"
                  className="px-6 py-6 text-center text-gray-400 italic"
                >
                  No hay productos agregados.
                </td>
              </tr>
            ) : (
              details.map((d, i) => (
                <tr
                  key={i}
                  className={`border-t ${i % 2 === 0 ? "bg-white" : "bg-gray-50"
                    }`}
                >
                  <td className="px-6 py-4">{d.productName}</td>
                  <td className="px-6 py-4">{d.brandName}</td>
                  <td className="px-6 py-4">{d.presentationName}</td>
                  <td className="px-6 py-4">{d.invimaCode}</td>
                  <td className="px-6 py-4">{d.batchNumber}</td>
                  <td className="px-6 py-4">
                    {new Date(d.expirationDate).toLocaleDateString("es-CO")}
                  </td>
                  <td className="px-6 py-4 flex items-center justify-center gap-2">
                    <button
                      onClick={() => decreaseQuantity(i)}
                      className="p-1 text-red-300 hover:bg-red-500 rounded"
                      title="Disminuir cantidad"
                    >
                      -
                    </button>
                    <span className="font-semibold text-blue-600">{d.quantity}</span>
                    <button
                      onClick={() => increaseQuantity(i)}
                      className="p-1 text-green-300 hover:bg-green-500 rounded"
                      title="Aumentar cantidad"
                    >
                      +
                    </button>
                  </td>
                  <td className="px-6 py-4">
                    <button
                      onClick={() => handleRemoveDetail(i)}
                      className="p-2 text-red-500 hover:bg-red-600 hover:text-white rounded-lg transition-all duration-200"
                      title="Eliminar de la lista"
                    >
                      <Trash2 size={18} />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {errors.details && (
        <p className="text-red-500 text-sm mt-3">{errors.details}</p>
      )}

      <div className="flex justify-end mt-6">
        <button
          onClick={handlePreSubmit}
          disabled={isSubmitting}
          className={`w-full md:w-auto px-6 py-2 rounded flex items-center justify-center gap-2 text-white transition ${isSubmitting ? "bg-gray-400 cursor-not-allowed" : "bg-green-600 hover:bg-green-700"
            }`}
        >
          <Save size={18} />
          {isSubmitting ? "Procesando..." : "Guardar Salida"}
        </button>
      </div>

      {/* --- MODAL DE CONFIRMACIÓN --- */}
      {showConfirmModal && (
        <div className="fixed inset-0 z- flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm p-4">
          <div className="bg-white p-8 rounded-2xl shadow-2xl max-w-sm w-full transform transition-all animate-in fade-in zoom-in duration-200">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
                <Save className="h-6 w-6 text-red-600" />
              </div>
              <h3 className="text-lg font-bold text-gray-900">¿Confirmar Salida?</h3>
              <p className="text-sm text-gray-500 mt-2">
                Se descontarán {details.length} productos del inventario actual.
              </p>
              <div className="mt-2 p-2 bg-gray-50 rounded-lg font-bold text-gray-700">
                Total: {formatCurrencyCOP(details.reduce((t, d) => t + (d.unitPrice * d.quantity), 0))}
              </div>
            </div>
            <div className="mt-6 flex gap-3">
              <button
                onClick={() => setShowConfirmModal(false)}
                className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-semibold transition"
              >
                Cancelar
              </button>
              <button
                onClick={handleSubmit}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-semibold transition shadow-md"
              >
                Sí, Confirmar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RegisterExit;